import { useEffect, useMemo, useRef, useState } from 'react';
import { LIMITES, type ComandoDeBarra } from '@remoto/protocolo';
import {
  anexarArquivo,
  descartarAnexos,
  SEM_ANEXOS,
  tirarAnexo,
  usarEnvios,
  type AnexoNaFila,
} from '../../sessoes/envios';
import { usarDedo } from '../../toque';
import { BotaoIcone } from '../layout';
import { IconeAnexar as IconeMais, IconeComandos, IconeEnviar } from '../icones';
import { BotaoDeDitado } from './BotaoDeDitado';
import { ChipDeAnexo } from './ChipDeAnexo';
import { inserirNoCursor } from './ditado';
import { completarComando, detectarBarra, filtrarComandos } from './barra-comandos';
import { realcarMarkdown, type Cor } from './realce';

/** A cor de cada tipo de trecho, no realce por baixo do campo. */
const CLASSE: Record<Cor, string> = {
  normal: 'text-zinc-100',
  titulo: 'font-semibold text-sky-300',
  forte: 'font-semibold text-zinc-50',
  enfase: 'italic text-zinc-300',
  codigo: 'rounded bg-zinc-800/70 text-amber-200',
  cerca: 'text-amber-200/80',
  citacao: 'text-zinc-400',
  marca: 'text-emerald-400',
  link: 'text-sky-400 underline',
};

export type { AnexoNaFila };

/**
 * As mesmas medidas nos dois: um pixel de diferença desalinha o cursor.
 *
 * O `pointer-coarse:text-base` também entra **nos dois**. No telefone o campo
 * precisa de 16 px — abaixo disso o Safari do iPhone dá zoom na página ao
 * focar e não desfaz —, e como quem mostra o texto é o `<pre>` de trás
 * enquanto o `<textarea>` fica transparente só com o cursor, subir a fonte de
 * um lado só deixaria o cursor andando longe da letra.
 */
const MEDIDAS = 'px-3 py-2 text-sm leading-6 font-sans pointer-coarse:text-base';

/**
 * O compositor da conversa.
 *
 * Duas coisas além de um campo de texto: o markdown sai realçado enquanto se
 * escreve, e o anexo entra por onde a pessoa tentar — colando, arrastando ou
 * pelo clipe.
 *
 * O realce é um `<pre>` colorido **atrás** de um `textarea` transparente, com
 * exatamente a mesma métrica. Trocar o campo por um editor de verdade daria
 * mais cor e tiraria o que importa mais: colar arquivo, arrastar, acentuação e
 * o Enter que envia.
 */
export function Compositor({
  sessaoId,
  texto,
  aoMudarTexto,
  aoEnviar,
  enviando,
  entregando = false,
  controles,
  nota,
  motivoSemComandos,
  semBorda = false,
  comandos = [],
  avisoDeImagem = null,
}: {
  sessaoId: string;
  texto: string;
  aoMudarTexto: (texto: string) => void;
  aoEnviar: (texto: string, anexos: { chave: string; mime: string; nome: string }[]) => void;
  enviando: boolean;
  /**
   * Há mensagem desta conversa com anexo ainda a caminho da máquina. Fecha o
   * Enviar: o daemon entrega cada mensagem assim que os anexos dela chegam, e
   * uma segunda, sem anexo, passaria na frente — o modelo leria "foque no
   * minuto 3" antes de "analise este vídeo".
   */
  entregando?: boolean;
  /** Modo, modelo e as ações da sessão: entram na linha de controles. */
  controles?: React.ReactNode;
  /**
   * Texto visível abaixo da caixa. Existe porque `title` não chega ao celular,
   * e é justamente no celular que a regra da casa manda a conversa morar.
   */
  nota?: React.ReactNode;
  /** Por que o botão `/` está desabilitado. Ausente = ele funciona. */
  motivoSemComandos?: string;
  /** Já há uma linha divisória logo acima; esta não precisa desenhar outra. */
  semBorda?: boolean;
  /** Comandos e skills da sessão, para a barra `/`. Vazio = sem menu. */
  comandos?: ComandoDeBarra[];
  /**
   * O que dizer quando há IMAGEM na fila — `avisoDeImagemDoMotor`. O clipe
   * aparece sempre (D23 revista): no OpenCode o turno com imagem vai ao modelo
   * de visão da frota, e a pessoa fica sabendo ANTES de mandar.
   */
  avisoDeImagem?: string | null;
}): React.JSX.Element {
  /*
    Os chips moram no store (`sessoes/envios.ts`), por conversa, e não aqui: no
    celular este componente só existe na aba `chat`, e um upload de 1 GB que
    morresse a cada olhada na lista de sessões não serviria a ninguém. Desmontar
    o compositor não aborta nada; o X do chip e `sair()` abortam.
  */
  const fila = usarEnvios((e) => e.anexos[sessaoId] ?? SEM_ANEXOS);
  const [arrastando, setArrastando] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [selecionado, setSelecionado] = useState(0);
  const campo = useRef<HTMLTextAreaElement>(null);
  const fundo = useRef<HTMLPreElement>(null);
  const seletor = useRef<HTMLInputElement>(null);
  const caixa = useRef<HTMLElement>(null);
  const dedo = usarDedo();

  /**
   * No telefone o compositor nasce deitado, de uma linha só.
   *
   * Aberto ele custava 238 px dos 844 da tela e sobravam 295 px de conversa —
   * 35%, medido. Como quem está de pé lê muito mais do que escreve, o padrão
   * inverteu: fechado ele ocupa uma linha, e só quem vai escrever paga o
   * espaço do campo, dos anexos e dos seletores.
   *
   * No mouse isso não existe: `compacto` só é verdade no dedo.
   */
  const [aberto, setAberto] = useState(false);
  const compacto = dedo && !aberto;

  /**
   * As ações da sessão (interromper, encerrar, modo, modelo) atrás de um ⋯.
   *
   * Elas são quatro peças largas, e no telefone empurravam a barra para
   * quatro linhas — 176 px de botão em cima de uma conversa que já é curta.
   * Só que são também as menos usadas: quem abre o campo abre para escrever,
   * não para trocar de modelo. No mouse continuam todas à vista, que lá
   * largura sobra.
   */
  const [acoesAbertas, setAcoesAbertas] = useState(false);

  function abrir(): void {
    if (!dedo || aberto) return;
    setAberto(true);
    focarEm('fim');
  }

  /** Fecha e devolve o teclado; o texto continua no campo. */
  function fechar(): void {
    setAberto(false);
    setAcoesAbertas(false);
    campo.current?.blur();
  }

  /**
   * Tocar fora fecha.
   *
   * `pointerdown`, e não `mousedown`: o iOS não sintetiza evento de mouse ao
   * tocar em elemento que não é clicável, então metade da tela não fecharia
   * o compositor. Só escuta quando está aberto — ouvinte parado no documento
   * a sessão inteira é desperdício.
   */
  useEffect(() => {
    if (!dedo || !aberto) return;
    const aoTocarFora = (evento: PointerEvent): void => {
      const alvo = evento.target as HTMLElement | null;
      /*
        ★ Menu em portal NÃO é "fora".

        O `Seletor` desenha o menu em `document.body` — teve de ser assim, senão
        o `overflow-hidden` da lateral o decepava. Mas ele continua sendo parte
        deste compositor, e este ouvinte não sabia disso: o primeiro
        `pointerdown` sobre uma opção caía fora da `caixa`, recolhia o
        compositor, desmontava as ações e com elas o próprio seletor — tudo
        ANTES de o `click` chegar ao `onClick` da opção.

        Resultado medido no aparelho do dono: trocar modo, modelo ou esforço no
        celular e no tablet não fazia nada, e nem erro aparecia, porque a
        requisição nunca chegava a sair. No mouse passava, porque este ouvinte
        só existe no dedo — e foi por isso que o ensaio de desktop ficou verde
        meses sobre um controle quebrado.
      */
      if (alvo?.closest?.('[data-popover]')) return;
      if (!caixa.current?.contains(alvo)) fechar();
    };
    document.addEventListener('pointerdown', aoTocarFora);
    return () => document.removeEventListener('pointerdown', aoTocarFora);
  }, [dedo, aberto]);

  /**
   * O menu da barra `/`: aberto quando a mensagem começa com barra e o cursor
   * ainda está no primeiro token. Toda a decisão é pura (`barra-comandos.ts`);
   * aqui só se desenha.
   */
  const barra = useMemo(() => {
    if (comandos.length === 0) return null;
    const detectada = detectarBarra(texto, cursor);
    if (!detectada) return null;
    const lista = filtrarComandos(comandos, detectada.termo);
    return lista.length > 0 ? { lista } : null;
  }, [comandos, texto, cursor]);

  // A lista muda, a seleção volta ao topo — apontar para um índice que já não
  // existe completaria o comando errado.
  useEffect(() => setSelecionado(0), [barra?.lista.length, texto]);

  /** A dica de argumento vira linha-fantasma depois de completar. */
  const dica = useMemo(() => {
    const casamento = /^\/(\S+)\s*$/.exec(texto);
    if (!casamento || barra) return '';
    const comando = comandos.find((c) => c.nome === casamento[1]);
    return comando?.dicaDeArgumento ?? '';
  }, [texto, comandos, barra]);

  /**
   * Põe o foco no campo com o cursor onde se pediu, depois do React pintar.
   *
   * `fim` significa fim do texto. O `requestAnimationFrame` não é enfeite: o
   * valor do textarea só existe depois do render, e mover o cursor antes disso
   * o deixa na posição antiga.
   */
  function focarEm(posicao: number | 'fim'): void {
    requestAnimationFrame(() => {
      const area = campo.current;
      if (!area) return;
      area.focus();
      const onde = posicao === 'fim' ? area.value.length : posicao;
      area.setSelectionRange(onde, onde);
      setCursor(onde);
    });
  }

  function completar(nome: string): void {
    aoMudarTexto(completarComando(texto, nome));
    // O cursor cai depois do espaço: o menu fecha sozinho e o argumento entra.
    focarEm('fim');
  }

  /**
   * O botão `/` não abre menu próprio: escreve a barra no campo e deixa o
   * `useMemo` de sempre decidir. Duas portas, uma lógica só — o teclado e o
   * clique não podem discordar sobre quando a lista aparece.
   */
  function alternarComandos(): void {
    if (barra) {
      // Fecha sem apagar, a mesma sentinela do Escape.
      setCursor(-1);
      return;
    }
    if (!texto.startsWith('/')) aoMudarTexto('/');
    focarEm(1);
  }

  /** Por que o `/` não está disponível agora; vazio = está. */
  const semComandos =
    motivoSemComandos ??
    (comandos.length === 0
      ? 'a máquina ainda não enviou a lista de comandos desta sessão'
      : texto.length > 0 && !texto.startsWith('/')
        ? 'comando de barra é a mensagem inteira — limpe o campo para usar /'
        : '');

  // O campo cresce com o texto, até um teto; passando dele, rola — e aí o
  // realce precisa rolar junto, senão a cor descola das letras.
  useEffect(() => {
    const area = campo.current;
    if (!area) return;
    area.style.height = 'auto';
    // Fechado, o campo vale uma linha: é a altura que sobra depois de somar o
    // `leading-6` (24 px) ao `py-2` (8 + 8) de MEDIDAS.
    area.style.height = (compacto ? 40 : Math.min(area.scrollHeight, 240)) + 'px';
    if (fundo.current) fundo.current.scrollTop = area.scrollTop;
  }, [texto, compacto]);

  function receber(arquivos: FileList | File[] | null): void {
    const lista = [...(arquivos ?? [])];
    if (lista.length === 0) return;
    const vagas = LIMITES.anexosPorMensagem - fila.length;
    for (const arquivo of lista.slice(0, Math.max(0, vagas))) void anexarArquivo(sessaoId, arquivo);
  }

  const prontos = fila.filter((a) => a.chave);
  const pendentes = fila.some((a) => !a.chave && !a.erro);
  const podeEnviar = texto.trim().length > 0 && !enviando && !pendentes && !entregando;

  function enviar(): void {
    if (!podeEnviar) return;
    aoEnviar(
      texto.trim(),
      prontos.map((a) => ({ chave: a.chave as string, mime: a.mime, nome: a.nome })),
    );
    descartarAnexos(sessaoId);
    // Mandou, some: quem está de pé volta a ler, e deixar o campo aberto
    // devolveria a conversa aos mesmos 35% de tela.
    if (dedo) fechar();
  }

  return (
    <section
      ref={caixa}
      className={
        'shrink-0 space-y-1.5 p-3 ' +
        (semBorda ? '' : 'border-t border-zinc-800') +
        // Fechado o respiro encolhe junto: 12 px em cima e embaixo de uma
        // barra de uma linha é moldura demais.
        (compacto ? ' !py-1.5' : '')
      }
      onDragOver={(e) => {
        e.preventDefault();
        setArrastando(true);
      }}
      onDragLeave={() => setArrastando(false)}
      onDrop={(e) => {
        e.preventDefault();
        setArrastando(false);
        receber(e.dataTransfer.files);
      }}
    >
      {/*
        A caixa. Campo e controles moram na mesma peça — é o que faz a barra
        parecer um controle só, em vez de um campo com botões soltos embaixo.

        PROIBIDO aqui: `overflow-hidden`, `transform`, `filter` e `isolate`.
        Qualquer um deles cria contexto de recorte novo e corta TRÊS popovers
        de uma vez — o menu da barra e os dois seletores que abrem para cima.
        Os cantos já saem certos sem recorte: nenhum filho encosta na borda.
      */}
      <div
        id="compositor"
        className={
          'relative rounded-xl border bg-zinc-950 transition ' +
          (arrastando ? 'border-emerald-600' : 'border-zinc-700 focus-within:border-zinc-500') +
          // Fechado vira uma linha só: campo à esquerda, ditado e enviar à
          // direita, na mesma altura. É a forma de todo compositor de celular,
          // e é o que devolve a tela para a conversa. `flex-wrap` por causa do
          // anexo: a lista de chips ocupa a linha de cima inteira (`w-full`), e
          // sem a quebra ela ficava AO LADO do campo, que encolhia a zero e
          // empurrava o enviar para fora da tela.
          (compacto ? ' flex flex-wrap items-center gap-1 pr-1' : '')
        }
      >
        {/* O menu da barra, filho da caixa e NUNCA do <pre>: dentro do realce
            ele entraria no fluxo do texto e desalinharia o cursor. */}
        {barra ? (
          <div
            role="listbox"
            aria-label="Comandos da sessão"
            className="absolute bottom-full left-0 right-0 z-30 mb-1 max-h-64 overflow-y-auto rounded-md border border-zinc-700 bg-zinc-900 py-1 shadow-lg shadow-black/50"
          >
            {barra.lista.map((comando, i) => (
              <button
                key={comando.nome}
                role="option"
                aria-selected={i === selecionado}
                // preventDefault no mousedown: o textarea não pode perder o foco.
                onMouseDown={(e) => {
                  e.preventDefault();
                  completar(comando.nome);
                }}
                onMouseEnter={() => setSelecionado(i)}
                className={
                  'flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-sm ' +
                  (i === selecionado ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-300')
                }
              >
                <span className="shrink-0 font-mono text-sky-300">/{comando.nome}</span>
                {comando.ehSkill ? (
                  <span className="shrink-0 rounded bg-emerald-900/50 px-1 text-[10px] uppercase text-emerald-300">
                    skill
                  </span>
                ) : null}
                <span className="min-w-0 flex-1 truncate text-xs text-zinc-500">
                  {comando.descricao}
                </span>
                {comando.dicaDeArgumento ? (
                  <span className="shrink-0 font-mono text-[11px] text-zinc-600">
                    {comando.dicaDeArgumento}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}

        {fila.length > 0 ? (
          <ul className="flex w-full flex-wrap gap-2 px-3 pt-2">
            {fila.map((anexo) => (
              <ChipDeAnexo
                key={anexo.id}
                anexo={anexo}
                aoTirar={() => tirarAnexo(sessaoId, anexo.id)}
              />
            ))}
          </ul>
        ) : null}
        {avisoDeImagem && fila.some((a) => a.mime.startsWith('image/') && !a.erro) ? (
          <p className="px-3 pt-1 text-[11px] text-sky-300" data-teste="aviso-de-imagem">
            {avisoDeImagem}
          </p>
        ) : null}

        {/* Daqui até o fim do textarea nada mudou, e nada pode mudar: o par
            <pre>/<textarea> compartilha MEDIDAS byte a byte, e o wrapper
            continua sem padding próprio. Mover o `px-3 py-2` para a caixa
            descolaria a cor das letras ao longo da linha. */}
        <div className={'relative ' + (compacto ? 'min-w-0 flex-1' : '')}>
          {/* O realce, atrás. `aria-hidden` porque quem lê a tela lê o campo. */}
          <pre
            ref={fundo}
            aria-hidden
            className={
              'pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words ' +
              MEDIDAS
            }
          >
            {realcarMarkdown(texto).map((trecho, i) => (
              <span key={i} className={CLASSE[trecho.cor]}>
                {trecho.texto}
              </span>
            ))}
            {/* Uma linha a mais: sem ela a última linha do campo fica sem fundo. */}
            {'\n'}
          </pre>

          <textarea
            ref={campo}
            value={texto}
            onChange={(e) => {
              aoMudarTexto(e.target.value);
              setCursor(e.target.selectionStart ?? e.target.value.length);
            }}
            // Quem abre a barra é o foco do campo, e só ele. Tocar no
            // microfone ou no enviar não abre nada: com a barra recolhida
            // esses dois seguem valendo, que é o caso de "mandar uma linha
            // rápida" — e é justamente o mais comum de pé.
            onFocus={abrir}
            onSelect={(e) => setCursor(e.currentTarget.selectionStart ?? 0)}
            onScroll={(e) => {
              if (fundo.current) fundo.current.scrollTop = e.currentTarget.scrollTop;
            }}
            onPaste={(e) => {
              const arquivos = [...e.clipboardData.files];
              if (arquivos.length > 0) {
                e.preventDefault();
                receber(arquivos);
              }
            }}
            onKeyDown={(e) => {
              // A guarda do menu vem ANTES da de envio: com a lista aberta,
              // Enter completa e NÃO envia — o Enter seguinte é que manda.
              if (barra) {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  const passo = e.key === 'ArrowDown' ? 1 : -1;
                  setSelecionado(
                    (atual) => (atual + passo + barra.lista.length) % barra.lista.length,
                  );
                  return;
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  const escolhido = barra.lista[selecionado];
                  if (escolhido) completar(escolhido.nome);
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  // Fechar sem apagar: o cursor sai do token e o menu some.
                  setCursor(-1);
                  return;
                }
              }
              // Enter manda; Shift+Enter quebra linha, como em qualquer chat.
              //
              // No dedo, não: o teclado virtual não tem Shift+Enter, então a
              // regra do desktop tornava **impossível** escrever uma mensagem
              // de duas linhas pelo telefone — cada retorno mandava o que
              // estava escrito pela metade. Ali o retorno quebra a linha e
              // quem manda é o botão, que é para onde o polegar vai de todo
              // jeito.
              if (e.key === 'Enter' && !e.shiftKey && !dedo) {
                e.preventDefault();
                enviar();
              }
            }}
            rows={3}
            placeholder="Escreva para esta sessão…"
            className={
              'relative block max-h-60 w-full resize-none overflow-y-auto bg-transparent text-transparent caret-zinc-100 outline-none placeholder:text-zinc-600 ' +
              MEDIDAS
            }
          />
        </div>

        {dica && !compacto ? (
          <p className="px-3 pb-1 text-[11px] text-zinc-600">
            este comando espera: <span className="font-mono text-zinc-500">{dica}</span>
          </p>
        ) : null}

        {/* No dedo as ações moram aqui, abertas pelo ⋯ da barra de baixo. */}
        {dedo && aberto && acoesAbertas && controles ? (
          <div className="flex flex-wrap items-center gap-1 border-t border-zinc-800 px-2 py-2">
            {controles}
          </div>
        ) : null}

        {/*
          A linha de controles. Dois grupos: o da esquerda encolhe e quebra
          (no celular, modo e modelo caem para a segunda linha); o da direita
          é `shrink-0` e nunca sai da primeira — enviar não pode sumir.

          Os recuos são ópticos: MEDIDAS tem `px-3` (12 px) e aqui `px-2` (8)
          mais o botão de 4 dão os mesmos 12 — o `+` nasce debaixo da primeira
          letra do texto.
        */}
        <div
          className={
            'flex gap-1 ' +
            // Fechado é uma linha só, e aí o alinhamento é ao centro. Aberto,
            // a esquerda quebra em até três linhas: sem `items-end` o botão de
            // enviar fica boiando na altura do meio, longe da última linha.
            (compacto ? 'shrink-0 items-center' : 'items-end px-2 pb-2')
          }
        >
          <div
            className={
              'min-w-0 flex-1 flex-wrap items-center gap-1 ' +
              // Fechado, todo o resto da barra sai: anexo, comandos, modo,
              // modelo e as ações da sessão. Quem quiser um deles toca no
              // campo e a barra volta inteira.
              (compacto ? 'hidden' : 'flex')
            }
          >
            <BotaoIcone
              rotulo="Anexar arquivo"
              onClick={() => seletor.current?.click()}
              disabled={fila.length >= LIMITES.anexosPorMensagem}
              {...(fila.length >= LIMITES.anexosPorMensagem
                ? { title: `limite de ${LIMITES.anexosPorMensagem} anexos por mensagem` }
                : {})}
            >
              <IconeMais />
            </BotaoIcone>
            <input
              ref={seletor}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                receber(e.target.files);
                e.target.value = '';
              }}
            />

            <BotaoIcone
              rotulo="Comandos"
              onClick={alternarComandos}
              disabled={Boolean(semComandos)}
              ligado={Boolean(barra)}
              {...(semComandos ? { title: semComandos } : {})}
            >
              <IconeComandos />
            </BotaoIcone>

            {dedo ? (
              controles ? (
                <BotaoIcone
                  rotulo="Ações da sessão"
                  ligado={acoesAbertas}
                  onClick={() => setAcoesAbertas((v) => !v)}
                >
                  ⋯
                </BotaoIcone>
              ) : null
            ) : (
              controles
            )}
          </div>

          <div className="flex shrink-0 items-center gap-1">
            {dedo && aberto ? (
              // Tocar fora também fecha; este é o alvo visível, para quem não
              // adivinha o gesto.
              <BotaoIcone rotulo="Fechar o campo" onClick={fechar}>
                ▾
              </BotaoIcone>
            ) : null}
            <BotaoDeDitado
              miudo={compacto}
              aoDitar={(ditado) => {
                // Onde o cursor estava, não no fim: quem dita no meio de uma
                // frase espera que caia ali.
                const onde = campo.current?.selectionStart ?? texto.length;
                aoMudarTexto(inserirNoCursor(texto, ditado, onde));
                focarEm(onde + ditado.length + 1);
              }}
            />
            <button
              onClick={enviar}
              disabled={!podeEnviar}
              aria-label="Enviar"
              title={
                pendentes
                  ? 'esperando o anexo subir'
                  : entregando
                    ? 'esperando a máquina receber os anexos'
                    : enviando
                      ? 'enviando…'
                      : texto.trim().length === 0
                        ? 'escreva algo antes de enviar'
                        : 'Enviar'
              }
              className={
                'flex shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-900 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40 ' +
                // Recolhido ele encolhe junto com o resto da barra; aberto,
                // volta a ser o alvo de 44 px que o polegar merece. A altura
                // sai inteira de um lado ou do outro: deixar `h-8` na base e
                // `h-7` no ramo faz as duas classes existirem juntas, e aí
                // quem decide é a ordem da folha, não este código.
                (compacto ? 'h-7 w-7' : 'h-8 w-8 pointer-coarse:h-11 pointer-coarse:w-11')
              }
            >
              <IconeEnviar />
            </button>
          </div>
        </div>
      </div>

      {nota ? <p className="px-1 text-[11px] text-zinc-600">{nota}</p> : null}
    </section>
  );
}
